import React, {useCallback, useEffect, useMemo, useState} from 'react'
import {KTIcon} from '../../../_metronic/helpers'
import {restRequest} from '../../../api-utils/apiQueries'
import UpdatedDriverPrice from './UpdatedDriverPrice'
import {
  centsToAud,
  formatAud,
  formatPricingLabel,
  PlatformPriceEntry,
} from './platformPricingHelpers'

const PlatformPricingWrapper = () => {
  const [driverPrice, setDriverPrice] = useState<PlatformPriceEntry[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState('')
  const [showUpdatedModal, setShowUpdatedModal] = useState(false)

  const fetchSettingsData = useCallback(async () => {
    setIsLoading(true)
    setError('')
    try {
      const response: any = await restRequest.getSettingsData()
      const data = response?.data?.data?.driver_price_suggested ?? {}
      const arrayOfObjects = Object.entries(data).map(([key, value]) => ({
        key,
        value: Number(value ?? 0),
      }))
      setDriverPrice(arrayOfObjects)
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Unable to load platform pricing.')
      setDriverPrice([])
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchSettingsData()
  }, [fetchSettingsData])

  const pricingStats = useMemo(() => {
    const amounts = driverPrice.map((item) => centsToAud(item.value))
    return {
      bands: driverPrice.length,
      min: amounts.length ? Math.min(...amounts) : 0,
      max: amounts.length ? Math.max(...amounts) : 0,
    }
  }, [driverPrice])

  return (
    <div className='d-flex flex-column gap-6'>
      <div className='card'>
        <div className='card-body p-8 p-xl-10'>
          <div className='d-flex flex-column flex-lg-row justify-content-between align-items-lg-start gap-6'>
            <div>
              <span className='badge badge-light-primary fw-bold mb-3'>Pricing settings</span>
              <h1 className='fw-bold text-gray-900 mb-2'>Platform Pricing</h1>
              <div className='text-muted fs-6 mw-700px'>
                Manage default driver suggested prices per seat by trip distance. These values are
                used when drivers do not set their own suggested pricing.
              </div>
            </div>
            <button
              type='button'
              className='btn btn-primary'
              onClick={() => setShowUpdatedModal(true)}
              disabled={isLoading || driverPrice.length === 0}
            >
              <KTIcon iconName='pencil' className='fs-2' />
              Edit pricing
            </button>
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className='card'>
          <div className='card-body d-flex flex-column align-items-center justify-content-center py-15'>
            <div className='spinner-border text-primary mb-4'></div>
            <div className='text-muted fw-semibold'>Loading platform pricing...</div>
          </div>
        </div>
      ) : error ? (
        <div className='alert alert-danger mb-0'>{error}</div>
      ) : (
        <>
          <div className='row g-6'>
            <div className='col-md-4'>
              <div className='card h-100'>
                <div className='card-body'>
                  <div className='text-muted fw-semibold fs-7 mb-2'>Fare bands</div>
                  <div className='fs-2 fw-bold text-gray-900'>{pricingStats.bands}</div>
                </div>
              </div>
            </div>
            <div className='col-md-4'>
              <div className='card h-100'>
                <div className='card-body'>
                  <div className='text-muted fw-semibold fs-7 mb-2'>Lowest price</div>
                  <div className='fs-2 fw-bold text-gray-900'>${pricingStats.min.toFixed(2)}</div>
                </div>
              </div>
            </div>
            <div className='col-md-4'>
              <div className='card h-100'>
                <div className='card-body'>
                  <div className='text-muted fw-semibold fs-7 mb-2'>Highest price</div>
                  <div className='fs-2 fw-bold text-gray-900'>${pricingStats.max.toFixed(2)}</div>
                </div>
              </div>
            </div>
          </div>

          <div className='card'>
            <div className='card-header border-0 pt-6 pb-0'>
              <div>
                <h3 className='card-title fw-bold text-gray-900 mb-1'>Driver suggested prices</h3>
                <div className='text-muted fs-7'>AUD per seat by distance band</div>
              </div>
            </div>
            <div className='card-body'>
              {driverPrice.length > 0 ? (
                <div className='row g-5'>
                  {driverPrice.map((item) => (
                    <div className='col-md-6 col-xl-4' key={item.key}>
                      <div className='rounded border border-gray-200 bg-white p-5 h-100'>
                        <div className='d-flex align-items-start justify-content-between gap-4'>
                          <div className='d-flex align-items-center gap-4 min-w-0'>
                            <span className='symbol symbol-44px bg-light-primary flex-shrink-0'>
                              <span className='symbol-label bg-light-primary'>
                                <KTIcon iconName='route' className='fs-2 text-primary' />
                              </span>
                            </span>
                            <div className='min-w-0'>
                              <div className='fw-bold text-gray-900 fs-6'>
                                {formatPricingLabel(item.key)}
                              </div>
                              <div className='text-muted fs-7'>Distance band</div>
                            </div>
                          </div>
                          <div className='text-end'>
                            <div className='fs-3 fw-bold text-gray-900'>{formatAud(item.value)}</div>
                            <div className='text-muted fs-8'>per seat</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className='text-center py-12'>
                  <div className='fw-bold text-gray-900 fs-5 mb-1'>No pricing configured</div>
                  <div className='text-muted fs-7'>Add distance pricing to activate suggestions.</div>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {showUpdatedModal && (
        <UpdatedDriverPrice
          showUpdatedModal={showUpdatedModal}
          toggleUpdatedModal={() => setShowUpdatedModal(false)}
          onSaved={fetchSettingsData}
          driverPrice={driverPrice}
        />
      )}
    </div>
  )
}

export default PlatformPricingWrapper
